import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Save, Eye, Loader2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { SectionCard, FieldRow, TextAreaRow, GalleryEditor, StringListEditor, SimpleAchievementEditor } from "@/components/EntityEditShared";
import { EntityLinksManager } from "@/components/EntityLinks";
import { showError } from "@/lib/errors";

export default function ClubEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [club, setClub] = useState(null);
  const [federations, setFederations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate(`/connexion?next=/clubs/${id}/edit`); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/clubs/${id}`);
        if (cancelled) return;
        const ok = user.id === data.owner_user_id || (user.roles || []).some((r) => ["admin", "copf"].includes(r));
        if (!ok) { toast.error("Accès réservé au propriétaire."); navigate(`/clubs/${data.slug}`); return; }
        setClub(data);
        const fr = await api.get(`/federations?limit=200`).catch(() => ({ data: { items: [] } }));
        if (!cancelled) setFederations(fr.data?.items || []);
      } catch (e) {
        if (e?.response?.status === 404) navigate("/clubs", { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, user, authLoading, navigate]);

  const upd = (patch) => setClub((c) => ({ ...c, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const fields = {
        name: club.name, logo_url: club.logo_url, cover_url: club.cover_url,
        description: club.description, short_motto: club.short_motto, founded_year: club.founded_year,
        discipline: club.discipline, disciplines: club.disciplines,
        island: club.island, commune: club.commune, address: club.address,
        contact_email: club.contact_email, contact_phone: club.contact_phone, website: club.website,
        social_facebook: club.social_facebook, social_instagram: club.social_instagram,
        federation_id: club.federation_id, gallery: club.gallery, achievements: club.achievements,
      };
      const { data } = await api.patch(`/clubs/${club.id}`, fields);
      setClub(data);
      toast.success("Fiche club sauvegardée.");
    } catch (e) {
      showError(e, { fallback: "Échec de la sauvegarde" });
    } finally { setSaving(false); }
  };

  if (loading || !club) return <div className="max-w-4xl mx-auto py-8 px-4 space-y-3"><Skeleton className="h-12 w-1/2" /><Skeleton className="h-64 w-full" /></div>;

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 space-y-5" data-testid="club-edit-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Link to={`/clubs/${club.slug}`}><Button variant="ghost" size="sm" data-testid="club-edit-back"><ArrowLeft className="h-4 w-4 mr-1" />Retour</Button></Link>
          <h1 className="font-display text-xl sm:text-2xl font-bold truncate flex items-center gap-2"><Building2 className="h-5 w-5 text-[hsl(var(--aito-lagoon))]" />Édition · {club.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/clubs/${club.slug}`}><Button variant="outline" size="sm" data-testid="club-edit-preview"><Eye className="h-4 w-4 mr-1.5" />Voir la fiche</Button></Link>
          <Button size="sm" onClick={save} disabled={saving} data-testid="club-edit-save" className="bg-[hsl(var(--aito-lagoon))] hover:bg-[hsl(var(--aito-lagoon-2))] text-black">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
            Sauvegarder
          </Button>
        </div>
      </div>

      <Tabs defaultValue="identity">
        <TabsList data-testid="club-edit-tabs">
          <TabsTrigger value="identity" data-testid="club-edit-tab-identity">Identité</TabsTrigger>
          <TabsTrigger value="contact" data-testid="club-edit-tab-contact">Contact</TabsTrigger>
          <TabsTrigger value="network" data-testid="club-edit-tab-network">Affiliation</TabsTrigger>
          <TabsTrigger value="palmares" data-testid="club-edit-tab-palmares">Palmarès collectif</TabsTrigger>
          <TabsTrigger value="gallery" data-testid="club-edit-tab-gallery">Galerie</TabsTrigger>
        </TabsList>

        <TabsContent value="identity" className="mt-4 space-y-4">
          <SectionCard title="Identité" description="Informations principales du club.">
            <div className="grid sm:grid-cols-2 gap-3">
              <FieldRow label="Nom du club" value={club.name} onChange={(v) => upd({ name: v })} placeholder="Spartacus Tahiti" testid="club-field-name" />
              <FieldRow label="Baseline / devise" value={club.short_motto} onChange={(v) => upd({ short_motto: v })} placeholder="L'esprit du combat" testid="club-field-motto" />
              <FieldRow label="Discipline principale" value={club.discipline} onChange={(v) => upd({ discipline: v })} placeholder="Ex. surf, va'a, athlétisme…" testid="club-field-discipline" />
              <FieldRow label="Année de fondation" type="number" value={club.founded_year || ""} onChange={(v) => upd({ founded_year: v ? parseInt(v, 10) : null })} placeholder="2012" testid="club-field-founded" />
              <FieldRow label="Île" value={club.island} onChange={(v) => upd({ island: v })} placeholder="Tahiti" testid="club-field-island" />
              <FieldRow label="Commune" value={club.commune} onChange={(v) => upd({ commune: v })} placeholder="Papeete" testid="club-field-commune" />
              <FieldRow label="Adresse" value={club.address} onChange={(v) => upd({ address: v })} placeholder="Rue, quartier, code postal" testid="club-field-address" />
              <FieldRow label="Logo (URL)" value={club.logo_url} onChange={(v) => upd({ logo_url: v })} placeholder="https://...logo.png" testid="club-field-logo" />
              <FieldRow label="Image de couverture (URL)" value={club.cover_url} onChange={(v) => upd({ cover_url: v })} placeholder="https://...cover.jpg" testid="club-field-cover" />
            </div>
          </SectionCard>
          <SectionCard title="Disciplines complémentaires" description="Disciplines secondaires proposées par le club.">
            <StringListEditor items={club.disciplines || []} onChange={(v) => upd({ disciplines: v })} placeholder="ex: judo, lutte, sambo" testid="club-disciplines" />
          </SectionCard>
          <SectionCard title="À propos" description="Présentation longue du club (histoire, valeurs, spécialités).">
            <TextAreaRow label="Description" value={club.description} onChange={(v) => upd({ description: v })} placeholder="Le Spartacus Tahiti a été fondé en 2012..." rows={6} testid="club-field-description" />
          </SectionCard>
        </TabsContent>

        <TabsContent value="contact" className="mt-4 space-y-4">
          <SectionCard title="Coordonnées" description="Comment vous contacter.">
            <div className="grid sm:grid-cols-2 gap-3">
              <FieldRow label="Email" type="email" value={club.contact_email} onChange={(v) => upd({ contact_email: v })} placeholder="contact@club.pf" testid="club-field-email" />
              <FieldRow label="Téléphone" value={club.contact_phone} onChange={(v) => upd({ contact_phone: v })} placeholder="+689 ..." testid="club-field-phone" />
              <FieldRow label="Site web" value={club.website} onChange={(v) => upd({ website: v })} placeholder="https://..." testid="club-field-website" />
              <FieldRow label="Facebook" value={club.social_facebook} onChange={(v) => upd({ social_facebook: v })} placeholder="https://facebook.com/..." testid="club-field-fb" />
              <FieldRow label="Instagram" value={club.social_instagram} onChange={(v) => upd({ social_instagram: v })} placeholder="https://instagram.com/..." testid="club-field-ig" />
            </div>
          </SectionCard>
        </TabsContent>

        <TabsContent value="network" className="mt-4 space-y-4">
          <SectionCard title="Fédération de tutelle" description="Une seule fédération peut superviser le club. Permet la validation transitive des liens.">
            <select
              data-testid="club-field-federation"
              value={club.federation_id || ""}
              onChange={(e) => upd({ federation_id: e.target.value || null })}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
            >
              <option value="">— Aucune fédération —</option>
              {federations.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </SectionCard>
          <SectionCard title="Liens sortants (vers la fédération)" description="Déclare officiellement l'affiliation à une ou plusieurs fédérations. Le lien devient public après validation par la fédération cible.">
            <EntityLinksManager sourceType="club" sourceId={club.id} />
          </SectionCard>
        </TabsContent>

        <TabsContent value="palmares" className="mt-4">
          <SectionCard title="Palmarès collectif" description="Titres remportés par le club (championnats, coupes, classements par équipe).">
            <SimpleAchievementEditor items={club.achievements || []} onChange={(v) => upd({ achievements: v })} />
          </SectionCard>
        </TabsContent>

        <TabsContent value="gallery" className="mt-4">
          <SectionCard title="Galerie photos" description="Ajoute des images de podiums, événements, locaux. Les URLs doivent être publiquement accessibles.">
            <GalleryEditor items={club.gallery || []} onChange={(v) => upd({ gallery: v })} />
          </SectionCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
